import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TurnstileWidget from "@/components/TurnstileWidget";
import ErrorSummary from "@/components/ErrorSummary";
import { subscribeToNewsletter } from "@/lib/adapters/newsletterApi";
import { LEGAL_FACTS_READY } from "@/lib/legalConfig";
import {
  NEWSLETTER_CONSENTS,
  NEWSLETTER_COPY as COPY,
  NEWSLETTER_DEFAULT_FREQUENCY,
  NEWSLETTER_FREQUENCIES,
  NEWSLETTER_UTILITIES,
  SUPPORT_EMAIL,
} from "@/lib/newsletterConfig";

// Same site-key mechanism as the bill check: unset means TurnstileWidget renders nothing and reports "settled".
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || null;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const ZIP_RE = /^\d{5}$/;

/** Plain-language message for a failed subscribe call. Never mentions providers, retries, or internal codes. */
export function describeSubscribeError(err) {
  switch (err && err.kind) {
    case "rate_limited": {
      const mins = err.retryAfterSeconds ? Math.max(1, Math.ceil(err.retryAfterSeconds / 60)) : null;
      return `There have been too many attempts. Wait ${mins ? `about ${mins} minute${mins === 1 ? "" : "s"}` : "a few minutes"} and select Subscribe again.`;
    }
    case "outdated":
      return "This page is out of date, so your request was not saved. Reload the page, then fill in the form again.";
    case "turnstile":
      return "We could not confirm that you are a person. Complete the check again, then select Subscribe.";
    case "validation":
      return "We could not accept those details. Check your email address and ZIP code, then select Subscribe again.";
    case "unavailable":
      return `Signup is not working right now, and your request was not saved. Your entries are still here. Select Subscribe to try again in a few minutes, or email ${SUPPORT_EMAIL}.`;
    case "unknown_outcome":
      return `We did not get an answer, so we cannot tell whether your request went through. Your entries are still here. Select Subscribe to send it again; if an email arrives, use the link in it. If this keeps happening, email ${SUPPORT_EMAIL}.`;
    default:
      return `Something went wrong and your request was not completed. Your entries are still here. Select Subscribe to try again, or email ${SUPPORT_EMAIL}.`;
  }
}

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} className="mt-1 text-sm text-[#B91C1C]" data-testid={id}>
      {children}
    </p>
  );
}

/**
 * Newsletter signup (CLO Newsletter_Legal_Review.md, approved copy used verbatim from lib/newsletterConfig.js).
 * Consent checkbox starts unchecked and is separate from any other consent. Double opt-in: success only ever says
 * "check your email", whether or not the address was already known (no enumeration).
 */
export default function NewsletterSignupForm({ siteKey = TURNSTILE_SITE_KEY, compact = false, defaultUtility = null, placement = null }) {
  // A page that already knows the visitor's utility (bill result, ESID lookup) passes it in. It only pre-selects the OPTIONAL
  // utility field: the visitor can change it, and once they touch the field a later change of the prop never overwrites it.
  const knownUtility = NEWSLETTER_UTILITIES.some((u) => u.value === defaultUtility) ? defaultUtility : "";
  const [email, setEmail] = useState("");
  const [utility, setUtilityState] = useState(knownUtility);
  const utilityTouched = useRef(false);
  const setUtility = (value) => { utilityTouched.current = true; setUtilityState(value); };
  useEffect(() => {
    if (!utilityTouched.current) setUtilityState(knownUtility);
  }, [knownUtility]);
  const [zip, setZip] = useState("");
  const [frequency, setFrequency] = useState(NEWSLETTER_DEFAULT_FREQUENCY);
  const [consent, setConsent] = useState(false);   // REQUIRED newsletter box: starts unticked
  const [offers, setOffers] = useState(false);     // OPTIONAL offers box: starts unticked, never gates the newsletter
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReady, setTurnstileReady] = useState(!siteKey);
  const [widgetKey, setWidgetKey] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | submitting | done
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  const submittingRef = useRef(false);
  const expiredRef = useRef(false);
  const summaryRef = useRef(null);
  const apiErrorRef = useRef(null);
  const doneHeadingRef = useRef(null);
  const emailRef = useRef(null);

  const [focusTarget, setFocusTarget] = useState(null);
  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget === "summary" && summaryRef.current) summaryRef.current.focus();
    if (focusTarget === "api" && apiErrorRef.current) apiErrorRef.current.focus();
    if (focusTarget === "done" && doneHeadingRef.current) doneHeadingRef.current.focus();
    if (focusTarget === "email" && emailRef.current) emailRef.current.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const validate = () => {
    const e = {};
    if (!email.trim()) e["nl-email"] = "Enter your email address.";
    else if (!EMAIL_RE.test(email.trim())) e["nl-email"] = "Enter a valid email address, like name@example.com.";
    if (zip.trim() && !ZIP_RE.test(zip.trim())) e["nl-zip"] = "Enter a 5-digit ZIP code, or leave it blank.";
    if (!frequency) e["nl-frequency"] = "Choose how often you want updates.";
    if (!consent) e["nl-consent"] = "Tick the required box to subscribe to the updates.";
    if (!turnstileReady) e["nl-turnstile"] = "Finish the check above, then select Subscribe.";
    return e;
  };

  const ERROR_LABELS = {
    "nl-email": "Email",
    "nl-zip": "ZIP code",
    "nl-frequency": "How often",
    "nl-consent": "Agreement",
    "nl-turnstile": "Check",
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submittingRef.current || phase === "submitting") return; // double click / double Enter
    setApiError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFocusTarget("summary");
      return;
    }
    submittingRef.current = true;
    setPhase("submitting");
    try {
      await subscribeToNewsletter({
        email: email.trim(),
        utility,
        zip: zip.trim(),
        frequency,
        consent,
        offersConsent: offers,
        placement,
        turnstileToken,
      });
      setPhase("done");
      setFocusTarget("done");
    } catch (err) {
      setApiError(err);
      setPhase("idle");
      if (err && (err.kind === "turnstile" || err.kind === "validation")) {
        // A used or rejected Turnstile token cannot be reused: fresh widget, fresh token.
        setTurnstileToken(null);
        expiredRef.current = false;
        if (siteKey) {
          setTurnstileReady(false);
          setWidgetKey((k) => k + 1);
        }
      }
      setFocusTarget("api");
    } finally {
      submittingRef.current = false;
    }
  };

  const startOver = () => {
    setEmail("");
    utilityTouched.current = false;
    setUtilityState(knownUtility);
    setZip("");
    setFrequency(NEWSLETTER_DEFAULT_FREQUENCY);
    setConsent(false);
    setOffers(false);
    setErrors({});
    setApiError(null);
    setTurnstileToken(null);
    setTurnstileReady(!siteKey);
    expiredRef.current = false;
    setWidgetKey((k) => k + 1);
    setPhase("idle");
    setFocusTarget("email");
  };

  // Inside a page block the page already has its own h2 (the block heading), so the success heading is one level lower.
  const SuccessHeading = compact ? "h3" : "h2";
  // Compact = two columns from md up; everything that is not a short field spans both.
  const wide = compact ? "md:col-span-2" : "";

  if (phase === "done") {
    return (
      <div role="status" className="rounded-2xl bg-slate-50 p-6" data-testid="newsletter-success">
        <SuccessHeading ref={doneHeadingRef} tabIndex={-1} className="font-display text-2xl font-extrabold tracking-tight text-[#0A2540] mb-3">
          {COPY.successHeading}
        </SuccessHeading>
        <p className="text-slate-700" data-testid="newsletter-success-text">{COPY.successBody}</p>
        <button type="button" onClick={startOver} className="btn-outline mt-5" data-testid="newsletter-start-over">
          Use a different email
        </button>
      </div>
    );
  }

  const summaryErrors = Object.keys(errors).map((id) => ({ id, label: ERROR_LABELS[id] }));
  const describedBy = (id, hint) => [errors[id] ? `${id}-error` : null, hint].filter(Boolean).join(" ") || undefined;
  const submitting = phase === "submitting";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className={compact ? "grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4" : "space-y-5"}
      data-testid="newsletter-form"
      aria-label={COPY.heading}
    >
      <div className={wide}><ErrorSummary ref={summaryRef} errors={summaryErrors} /></div>

      {apiError && (
        <div
          ref={apiErrorRef}
          tabIndex={-1}
          role="alert"
          className={`border-2 border-[#B91C1C] bg-[#FEF2F2] text-[#B91C1C] rounded-xl p-4 ${wide}`}
          data-testid={`newsletter-error-${apiError.kind}`}
        >
          {describeSubscribeError(apiError)}
          {apiError.kind === "outdated" && (
            <button type="button" onClick={() => window.location.reload()} className="btn-outline mt-3" data-testid="newsletter-reload">
              Reload the page
            </button>
          )}
        </div>
      )}

      <div className={wide}>
        <label htmlFor="nl-email" className="text-overline text-zinc-500 mb-1 block">
          {COPY.emailLabel} <span className="normal-case">(required)</span>
        </label>
        <input
          ref={emailRef}
          id="nl-email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          aria-required="true"
          aria-invalid={errors["nl-email"] ? "true" : undefined}
          aria-describedby={describedBy("nl-email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-brutal"
          data-testid="newsletter-email"
        />
        <FieldError id="nl-email-error">{errors["nl-email"]}</FieldError>
      </div>

      <div>
        <label htmlFor="nl-utility" className="text-overline text-zinc-500 mb-1 block">
          {COPY.utilityLabel} <span className="normal-case">(optional)</span>
        </label>
        <select
          id="nl-utility"
          name="utility"
          value={utility}
          onChange={(e) => setUtility(e.target.value)}
          className="input-brutal"
          data-testid="newsletter-utility"
        >
          <option value="">Select your utility</option>
          {NEWSLETTER_UTILITIES.map((u) => (
            <option key={u.value} value={u.value}>{u.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="nl-frequency" className="text-overline text-zinc-500 mb-1 block">
          {COPY.frequencyLabel} <span className="normal-case">(required)</span>
        </label>
        <select
          id="nl-frequency"
          name="frequency"
          required
          aria-required="true"
          aria-invalid={errors["nl-frequency"] ? "true" : undefined}
          aria-describedby={describedBy("nl-frequency")}
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          className="input-brutal"
          data-testid="newsletter-frequency"
        >
          {!NEWSLETTER_DEFAULT_FREQUENCY && <option value="">Choose how often</option>}
          {NEWSLETTER_FREQUENCIES.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <FieldError id="nl-frequency-error">{errors["nl-frequency"]}</FieldError>
      </div>

      <div>
        <label htmlFor="nl-zip" className="text-overline text-zinc-500 mb-1 block">
          {COPY.zipLabel} <span className="normal-case">(optional)</span>
        </label>
        <input
          id="nl-zip"
          name="zip"
          type="text"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          aria-invalid={errors["nl-zip"] ? "true" : undefined}
          aria-describedby={describedBy("nl-zip", "nl-zip-hint")}
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
          className="input-brutal"
          data-testid="newsletter-zip"
        />
        <p id="nl-zip-hint" className="mt-1 text-sm text-slate-600">{COPY.zipHint}</p>
        <FieldError id="nl-zip-error">{errors["nl-zip"]}</FieldError>
      </div>

      <fieldset className={`space-y-3 ${wide}`} data-testid="newsletter-consents">
        <legend className="sr-only">Your choices</legend>

        <div className="flex items-start gap-3">
          <input
            id="nl-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            aria-required="true"
            aria-invalid={errors["nl-consent"] ? "true" : undefined}
            aria-describedby={errors["nl-consent"] ? "nl-consent-error" : undefined}
            className="mt-1 w-5 h-5 shrink-0"
            data-testid="newsletter-consent"
          />
          <div>
            <label htmlFor="nl-consent" className="text-sm text-slate-700" data-testid="newsletter-consent-label">
              <strong>{NEWSLETTER_CONSENTS.newsletter.lead}</strong> {NEWSLETTER_CONSENTS.newsletter.rest}
            </label>
            <FieldError id="nl-consent-error">{errors["nl-consent"]}</FieldError>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <input
            id="nl-offers"
            name="offers_consent"
            type="checkbox"
            checked={offers}
            onChange={(e) => setOffers(e.target.checked)}
            className="mt-1 w-5 h-5 shrink-0"
            data-testid="newsletter-offers"
          />
          <label htmlFor="nl-offers" className="text-sm text-slate-700" data-testid="newsletter-offers-label">
            <strong>{NEWSLETTER_CONSENTS.offers.lead}</strong> {NEWSLETTER_CONSENTS.offers.rest}
          </label>
        </div>

        <p className="text-sm text-slate-700" data-testid="newsletter-notice">
          {COPY.noticeBeforeLink}{" "}
          {LEGAL_FACTS_READY ? (
            <Link to="/privacy" target="_blank" rel="noopener" className="underline" data-testid="newsletter-privacy-link">
              {COPY.privacyLinkText}
            </Link>
          ) : (
            <span className="text-slate-500">{COPY.privacyLinkText} (coming soon)</span>
          )}
        </p>
      </fieldset>

      <div className={wide}>
        <TurnstileWidget
          key={widgetKey}
          siteKey={siteKey}
          onVerify={(token) => {
            // The widget reports an expired challenge as onExpire() then onVerify(null); that null is NOT the
            // "no site key / script blocked" skip, so it must not mark the check as satisfied.
            if (!token && expiredRef.current) return;
            expiredRef.current = false;
            setTurnstileToken(token);
            setTurnstileReady(true);
          }}
          onExpire={() => { expiredRef.current = true; setTurnstileToken(null); setTurnstileReady(false); }}
        />
        <FieldError id="nl-turnstile-error">{errors["nl-turnstile"]}</FieldError>
      </div>

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="btn-green w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="newsletter-submit"
      >
        {submitting ? "Subscribing…" : COPY.button}
      </button>
      <p className="sr-only" role="status" data-testid="newsletter-busy-status">
        {submitting ? "Sending your request." : ""}
      </p>

      <p className={`text-xs text-slate-600 max-w-[60ch] ${wide}`} data-testid="newsletter-footer-line">{COPY.footerLine}</p>
    </form>
  );
}
